// src/components/layout/PageFooter.jsx
import React, { useRef } from 'react'
import { motion as Motion, useInView } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useAppDispatch.js'
import './PageFooter.scss'

// Import assets
import Group26 from '../../assets/Group26.png'; // Commented out as likely missing, verify existence
import FooterCircle from './../../assets/Images/Footer/Frame 1116607155.svg'

import {
    FaTwitter,
    FaLinkedin,
    FaGithub,
    FaInstagram,
    FaFacebook,
    FaYoutube
} from 'react-icons/fa'

export default function PageFooter() {
    const sectionRef = useRef(null)
    const navigate = useNavigate()
    const isInView = useInView(sectionRef, { amount: 0.2, once: true })

    // Get data from Redux store
    const { footer, loading } = useAppSelector((state) => state.global)

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                when: "beforeChildren"
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    }

    // Helper function to get social icon component
    const getSocialIcon = (platform, iconKey) => {
        const platformLower = platform?.toLowerCase() || iconKey?.toLowerCase() || ''

        switch (platformLower) {
            case 'twitter': case 'x':
                return <FaTwitter size={20} />
            case 'linkedin':
                return <FaLinkedin size={20} />
            case 'github':
                return <FaGithub size={20} />
            case 'instagram':
                return <FaInstagram size={20} />
            case 'facebook':
                return <FaFacebook size={20} />
            case 'youtube':
                return <FaYoutube size={20} />
            default:
                return <FaLink size={20} />
        }
    }

    // Show loading skeleton
    if (loading || !footer) {
        return (
            <footer className="footer bg-dark-light pt-16 text-white" ref={sectionRef}>
                {/* Loading skeleton */}
                <div className="footer-top container mx-auto px-4 md:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between py-8 md:py-12">
                    <div className="footer-top-left text-center md:text-left mb-8 md:mb-0">
                        <div className="footer-title text-3xl sm:text-4xl md:text-5xl font-light mb-4 bg-black-200 rounded h-12 animate-pulse w-64"></div>
                        <div className="footer-description text-gray-400 max-w-md mx-auto md:mx-0 bg-black-200 rounded h-4 animate-pulse w-48"></div>
                    </div>
                    <div className="footer-top-right">
                        <div className="footer-circle w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 flex items-center justify-center rounded-full bg-black-200 animate-pulse"></div>
                    </div>
                </div>

                <div className="footer-middle bg-dark rounded-lg mx-4 md:mx-auto my-8 md:my-12 py-8 px-4 md:px-8 max-w-7xl">
                    <div className="footer-links-container mx-auto">
                        <div className="footer-links-row flex flex-col md:flex-row flex-wrap">
                            {[...Array(4)].map((_, index) => (
                                <div key={index} className={`footer-links-col ${index === 0 ? 'wide w-full md:w-2/5 lg:w-2/5' : 'w-full md:w-1/5 lg:w-1/5'} px-0 md:px-4 mb-8 md:mb-0`}>
                                    <div className="text-lg font-light mb-4 bg-black-200 rounded h-6 w-32 animate-pulse"></div>
                                    <div className="space-y-2">
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="bg-black-200 rounded h-4 w-24 animate-pulse"></div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
        )
    }

    return (
        <footer className="footer bg-dark-light pt-16 text-white" ref={sectionRef}>
            {/* Top Section */}
            <Motion.div
                className="footer-top flex flex-col md:flex-row items-center justify-between py-8 md:py-12"
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={containerVariants}
            >
                <Motion.div
                    className="footer-top-left text-center md:text-left mb-8 md:mb-0"
                    variants={itemVariants}
                >
                    <h1 className="footer-title text-5xl sm:text-5xl md:text-5xl font-light mb-4">
                        {footer.heading?.toUpperCase() || "Let's work together"}
                    </h1>
                    <p className="footer-description text-gray-400 max-w-md mx-auto md:mx-0">
                        {footer.subheading || "Have a project in mind? Let's create something amazing."}
                    </p>
                </Motion.div>
                <Motion.div
                    className="footer-top-right"
                    variants={itemVariants}
                    transition={{ delay: 0.1 }}
                >
                    <div
                        onClick={() => {
                            if (window.location.pathname === "/contact") {
                                window.scrollTo({
                                    top: 0,
                                    behavior: "smooth",
                                })
                            }
                            navigate("/contact")
                        }}
                        className="footer-circle w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 flex items-center justify-center rounded-full bg-primary hover:bg-primary-dark transition-all duration-300 cursor-pointer"
                    >
                        <img src={FooterCircle} alt="Contact us" />
                    </div>
                </Motion.div>
            </Motion.div>

            {/* Middle Section - Links */}
            <Motion.div
                className="footer-middle bg-dark rounded-lg mx-4 md:mx-auto my-8 md:my-12 py-8 px-4 md:px-8 max-w-7xl"
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={containerVariants}
                transition={{ delay: 0.2 }}
            >
                <div className="footer-links-container mx-auto">
                    {/* First Row */}
                    <Motion.div
                        className="footer-links-row flex flex-col md:flex-row flex-wrap"
                        variants={containerVariants}
                    >
                        {/* About/Logo Column */}
                        <Motion.div
                            className="footer-links-col wide w-full md:w-2/5 lg:w-2/5 px-0 md:px-4 mb-8 md:mb-0"
                            variants={itemVariants}
                        >
                            <div className="mb-4">
                                <h3 className="text-2xl font-bold text-white">LogiQlink</h3>
                            </div>
                            <p className="text-gray-400 mb-6">
                                {footer.aboutText || "We are a team of passionate developers and designers creating innovative solutions."}
                            </p>
                            <div className="social-links flex space-x-4">
                                {footer.socialIcons?.map((social, index) => (
                                    <a
                                        key={index}
                                        href={social.url || "#"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-400 hover:text-accent transition-colors duration-300"
                                        aria-label={social.platform}
                                    >
                                        {getSocialIcon(social.platform, social.iconKey)}
                                    </a>
                                ))}
                            </div>
                        </Motion.div>

                        {/* Dynamic Columns */}
                        {footer.columns?.map((column, index) => (
                            <React.Fragment key={index}>{column.title != "Industries" && <Motion.div
                                key={column.key || index}
                                className="footer-links-col w-full md:w-1/5 lg:w-1/5 px-0 md:px-4 mb-8 md:mb-0"
                                variants={itemVariants}
                                transition={{ delay: 0.1 + (index * 0.05) }}
                            >
                                <h3 className="text-lg font-light mb-4 text-white">
                                    {column.title}
                                </h3>
                                <ul className="space-y-2">
                                    {column.links?.slice(0, 4).map((item, itemIndex) => (
                                        <li key={itemIndex}>
                                            <button
                                                onClick={() => {
                                                    window.scrollTo({
                                                        top: 0,
                                                        behavior: "smooth",
                                                    })
                                                    navigate(item.path)
                                                }}
                                                className="text-gray-400 hover:text-accent transition-colors duration-300 text-left w-full hover:underline"
                                            >
                                                {item.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </Motion.div>}</React.Fragment>
                        ))}
                    </Motion.div>

                    <Motion.hr
                        className="border-gray-700 my-8 mb-10"
                        initial={{ width: 0 }}
                        animate={isInView ? { width: "100%" } : {}}
                        transition={{ delay: 0.3, duration: 0.8 }}
                    />

                    {/* Second Row - Copyright and Legal Links */}
                    <Motion.div
                        className="footer-links-row flex flex-col md:flex-row justify-between mb-10 items-center"
                        initial="hidden"
                        animate={isInView ? "visible" : "hidden"}
                        variants={containerVariants}
                        transition={{ delay: 0.4 }}
                    >
                        <Motion.div
                            className="footer-links-col-half w-full md:w-auto mb-4 md:mb-0"
                            variants={itemVariants}
                        >
                            <p className="copyright text-gray-500 text-sm">
                                {footer.copyrightText || `© ${new Date().getFullYear()} LogiQlink. All rights reserved.`}
                            </p>
                        </Motion.div>
                        <Motion.div
                            className="footer-links-col-half justify-self-end w-full md:w-auto"
                            variants={itemVariants}
                        >
                            <div className="flex footer-links-col-flex justify-center md:justify-end text-sm text-gray-500">
                                {footer.privacyPolicyLink && (
                                    <button
                                        onClick={() => navigate(footer.privacyPolicyLink)}
                                        className="hover:text-accent transition-colors"
                                    >
                                        Privacy Policy
                                    </button>
                                )}
                                {footer.termsLink && (
                                    <>
                                        <span>|</span>
                                        <button
                                            onClick={() => navigate(footer.termsLink)}
                                            className="hover:text-accent transition-colors"
                                        >
                                            Terms of Service
                                        </button>
                                    </>
                                )}
                                {footer.cookiesLink && (
                                    <>
                                        <span>|</span>
                                        <button
                                            onClick={() => navigate(footer.cookiesLink)}
                                            className="hover:text-accent transition-colors"
                                        >
                                            Cookie Policy
                                        </button>
                                    </>
                                )}
                            </div>
                        </Motion.div>
                    </Motion.div>
                </div>
                <Motion.div className="footer-bottom-img mt-8 text-center">
                    <Motion.img
                        src={Group26}
                        alt="Footer decoration"
                        className="mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.5 }}
                    />
                </Motion.div>
            </Motion.div>
        </footer>
    )
}